
import { Link } from './SmartLink';
import { Phone, Envelope, WhatsappLogo, MapPin, ArrowRight, FacebookLogo, InstagramLogo, ShieldCheck, Clock, Star, CurrencyDollar } from '@phosphor-icons/react';
import { COMPANY } from '../lib/company';

const QUICK_LINKS = [
  { n: 'Home',                   p: '/' },
  { n: 'About Us',               p: '/about' },
  { n: 'Holiday Packages',       p: '/tour-packages' },
  { n: 'UAE Tours',              p: '/uae-tours' },
  { n: 'Air Tickets',            p: '/flights' },
  { n: 'Hotel Booking',          p: '/hotels' },
  { n: 'Umrah Packages',         p: '/umrah' },
  { n: 'Corporate Travel',       p: '/corporate-travel' },
  { n: 'Travel Blog',            p: '/blog' },
  { n: 'Contact',                p: '/contact' },
];

const SERVICE_LINKS = [
  { n: 'Visa Services',          p: '/visas' },
  { n: 'UAE Visit Visa',         p: '/visas/uae-visit-visa' },
  { n: 'Schengen Visa',          p: '/visas/schengen-visa' },
  { n: 'UK Visa',                p: '/visas/uk-visa' },
  { n: 'USA Visa',               p: '/visas/usa-visa' },
  { n: 'Canada Visa',            p: '/visas/canada-visa' },
  { n: 'Australia Visa',         p: '/visas/australia-visa' },
  { n: 'Travel Insurance',       p: '/insurance' },
  { n: 'Corporate Travel',       p: '/corporate-travel' },
];

const POLICY_LINKS = [
  { n: 'Privacy Policy',          p: '/privacy-policy' },
  { n: 'Terms & Conditions',      p: '/terms' },
  { n: 'Refund & Cancellation',   p: '/refund-policy' },
  { n: 'Cookie Policy',           p: '/cookie-policy' },
];

// Honest trust badges - only factual claims
const TRUST_BADGES = [
  { icon: MapPin,         label: 'UAE-Based Agency' },
  { icon: ShieldCheck,    label: 'Trade Licensed' },
  { icon: Star,           label: 'Trusted by UAE Travellers' },
  { icon: CurrencyDollar, label: 'Transparent Pricing' },
  { icon: Clock,          label: 'Support 7 Days/Week' },
];

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer>
      {/* --- Newsletter Band ----------------------------------------- */}
      <div className="px-8 py-12" style={{ background: '#15355A' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h3 className="font-display font-black text-white text-xl mb-1">
              Get exclusive travel deals in your inbox.
            </h3>
            <p className="text-white/75 text-[13px] font-medium">
              No spam - only real offers, visa updates, and destination guides.
            </p>
          </div>
          <form
            className="flex gap-3 w-full max-w-md"
            onSubmit={e => e.preventDefault()}
          >
            <input
              type="email"
              placeholder="Your email address"
              className="flex-1 bg-white/10 border border-white/15 rounded-xl px-4 py-3 text-white placeholder:text-white/35 text-[13px] font-medium outline-none focus:border-[#0096FF]"
            />
            <button
              type="submit"
              className="bg-[#0077CC] text-white px-6 py-3 rounded-xl font-black text-[11px] uppercase tracking-wider hover:bg-white hover:text-[#0068B3] transition-all whitespace-nowrap"
            >
              Subscribe
            </button>
          </form>
        </div>
      </div>

      {/* --- Main Footer Body ---------------------------------------- */}
      <div className="px-8 pt-16 pb-10" style={{ background: '#071126' }}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-white/5">

          {/* Column 1 - Brand + Contact */}
          <div className="space-y-6">
            <Link
              to="/"
              className="inline-flex w-fit rounded-2xl bg-white px-4 py-3 shadow-xl shadow-black/20 transition-transform duration-200 hover:scale-[1.02]"
              aria-label="Big Bird Travel home"
            >
              <img
                src="/logo.png"
                alt="Big Bird Travel and Tourism"
                className="h-16 w-auto max-w-[190px] object-contain sm:h-20 sm:max-w-[220px]" loading="lazy" />
            </Link>

            <p className="text-white/70 text-[13px] leading-relaxed font-medium">
              UAE-based travel agency serving residents and nationals across the Emirates. Visa assistance, holiday packages, Umrah, and UAE tours - all from one team.
            </p>

            <div className="space-y-2.5">
              <a
                href={`tel:${COMPANY.phone}`}
                className="flex items-center gap-3 text-white/75 hover:text-[#3FA9F5] transition-colors text-[13px] font-medium"
              >
                <Phone size={14} weight="fill" className="text-[#3FA9F5] shrink-0" />
                {COMPANY.phone}
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-3 text-white/75 hover:text-[#3FA9F5] transition-colors text-[13px] font-medium"
              >
                <Envelope size={14} weight="fill" className="text-[#3FA9F5] shrink-0" />
                {COMPANY.email}
              </a>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/75 hover:text-[#3FA9F5] transition-colors text-[13px] font-medium"
              >
                <WhatsappLogo size={14} weight="fill" className="text-[#3FA9F5] shrink-0" />
                {COMPANY.whatsappDisplay} (WhatsApp)
              </a>
            </div>

            <div className="flex gap-2.5">
              {[
                { Icon: FacebookLogo, href: COMPANY.social.facebook, label: 'Facebook' },
                { Icon: InstagramLogo, href: COMPANY.social.instagram, label: 'Instagram' },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-full bg-white/5 border border-white/8 flex items-center justify-center text-white/75 hover:bg-[#0077CC] hover:text-white hover:border-[#0077CC] transition-all"
                >
                  <Icon size={15} weight="fill" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 - Packages & Tours */}
          <div>
            <h4 className="text-[9px] font-black uppercase tracking-[0.35em] text-[#3FA9F5] mb-5">
              Packages & Tours
            </h4>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map(l => (
                <li key={l.n}>
                  <Link
                    to={l.p}
                    className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-[13px] font-medium group"
                  >
                    <ArrowRight
                      size={11}
                      weight="bold"
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#3FA9F5] shrink-0"
                    />
                    {l.n}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Visa & Services */}
          <div>
            <h4 className="text-[9px] font-black uppercase tracking-[0.35em] text-[#3FA9F5] mb-5">
              Visa &amp; Services
            </h4>
            <ul className="space-y-2.5">
              {SERVICE_LINKS.map(l => (
                <li key={l.n}>
                  <Link
                    to={l.p}
                    className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-[13px] font-medium group"
                  >
                    <ArrowRight
                      size={11}
                      weight="bold"
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#3FA9F5] shrink-0"
                    />
                    {l.n}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Office + Hours */}
          <div className="space-y-6">
            <div>
              <h4 className="text-[9px] font-black uppercase tracking-[0.35em] text-[#3FA9F5] mb-5">
                Our Office
              </h4>
              <a
                href={COMPANY.address.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex gap-3 group"
              >
                <MapPin size={15} weight="fill" className="text-[#3FA9F5] shrink-0 mt-0.5" />
                <address className="not-italic text-[13px] text-white/70 font-medium leading-relaxed group-hover:text-white/70 transition-colors">
                  {COMPANY.address.line1}<br />
                  {COMPANY.address.line2}<br />
                  {COMPANY.address.city},<br />
                  {COMPANY.address.country}
                </address>
              </a>
            </div>

            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.3em] text-white/60 mb-2">Registration</p>
              <p className="text-[11px] text-white/60 font-medium leading-relaxed">
                Trade License: {COMPANY.registration.tradeLicense}
              </p>
            </div>

            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#0077CC] text-white px-5 py-3 rounded-xl font-black text-[11px] uppercase tracking-wider hover:bg-white hover:text-[#0068B3] transition-all shadow-lg"
            >
              Get a Free Quote <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>

        {/* --- Honest Trust Badge Strip -------------------------------- */}
        <div className="flex flex-wrap items-center justify-center gap-6 py-8 border-b border-white/5">
          {TRUST_BADGES.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2">
              <Icon size={14} weight="fill" className="text-[#3FA9F5]" />
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/60">{label}</span>
            </div>
          ))}
        </div>

        {/* --- Bottom Bar ------------------------------------------- */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-6">
          <span className="text-[9px] font-black uppercase tracking-[0.25em] text-white/60">
            &copy; {year} {COMPANY.name}. All rights reserved.
          </span>
          <div className="flex flex-wrap justify-center gap-4 gap-y-2">
            {POLICY_LINKS.map(l => (
              <Link
                key={l.n}
                to={l.p}
                className="text-[9px] font-black uppercase tracking-[0.25em] text-white/60 hover:text-[#3FA9F5] transition-colors"
              >
                {l.n}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile spacing for mobile bottom bar */}
      <div className="h-16 md:hidden" style={{ background: '#071126' }} />
    </footer>
  );
}
